import React, { useState, useContext, useEffect, useRef } from "react";
import {
  AlertTriangle,
  MessageSquare,
  Clock,
  CheckCircle,
  X,
  Send,
  User,
  ShieldCheck,
} from "lucide-react";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout ";
import "../../../assets/styles/admin/AdminDisputes.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

/* ══════════════════════════════════════════════
   MOCK DATA  (fallback when API unavailable)
══════════════════════════════════════════════ */
const MOCK_DISPUTES = [
  {
    id: "D-1024",
    status: "open",
    priority: "high",
    title: "Wrong quantity recorded",
    customer: "Ali Raza",
    against: "Ahmad Dairy",
    date: "Feb 15, 2026",
    description: "Customer claims 2L was delivered instead of 3L on Feb 14.",
  },
  {
    id: "D-1023",
    status: "in-review",
    priority: "medium",
    title: "Overcharged for milk",
    customer: "Fatima Bibi",
    against: "Hassan Milk Supply",
    date: "Feb 14, 2026",
    description: "Customer was billed Rs 200 extra on the monthly invoice.",
  },
  {
    id: "D-1022",
    status: "resolved",
    priority: "high",
    title: "Milk quality complaint",
    customer: "Usman Ahmed",
    against: "Karachi Dairy Hub",
    date: "Feb 13, 2026",
    description: "Customer reported sour milk delivered on Feb 12.",
  },
  {
    id: "D-1021",
    status: "open",
    priority: "low",
    title: "Missed delivery",
    customer: "Zainab Fatima",
    against: "Bilal Fresh Milk",
    date: "Feb 12, 2026",
    description: "No milk delivered on Feb 11 without any prior notice.",
  },
];

const MOCK_TICKETS = [
  {
    id: "T-2048",
    status: "open",
    priority: "high",
    title: "Cannot scan QR codes",
    customer: "Bilal Fresh Milk",
    date: "Feb 15, 2026",
    description: "QR code scanner not working on distributor app after update.",
  },
  {
    id: "T-2047",
    status: "in-review",
    priority: "medium",
    title: "Payment not reflecting",
    customer: "Ayesha Khan",
    date: "Feb 14, 2026",
    description: "Payment made 2 days ago still not showing in dashboard.",
  },
  {
    id: "T-2046",
    status: "open",
    priority: "low",
    title: "Need plan downgrade",
    customer: "Punjab Dairy Farm",
    date: "Feb 13, 2026",
    description: "Customer wants to downgrade from Premium to Basic plan.",
  },
];

/* Mock chat messages per item id */
const MOCK_MESSAGES = {
  "D-1024": [
    {
      id: 1,
      sender: "customer",
      name: "Ali Raza",
      text: "I only received 2L but I subscribed for 3L daily.",
      time: "10:05 AM",
    },
    {
      id: 2,
      sender: "admin",
      name: "Admin",
      text: "We have noted your complaint and are reviewing the delivery logs.",
      time: "10:12 AM",
    },
    {
      id: 3,
      sender: "customer",
      name: "Ali Raza",
      text: "Please resolve this quickly, it's been 2 days.",
      time: "11:00 AM",
    },
  ],
  "D-1023": [
    {
      id: 1,
      sender: "customer",
      name: "Fatima Bibi",
      text: "I was charged Rs 200 extra this month. Please check.",
      time: "9:00 AM",
    },
    {
      id: 2,
      sender: "admin",
      name: "Admin",
      text: "We're reviewing your billing. Will update you shortly.",
      time: "9:30 AM",
    },
  ],
};

/* ══════════════════════════════════════════════
   SMALL HELPERS
══════════════════════════════════════════════ */
const STATUS_MAP = {
  open: { cls: "adminDisputes-badge-open", label: "Open" },
  "in-review": { cls: "adminDisputes-badge-in-review", label: "In Review" },
  resolved: { cls: "adminDisputes-badge-resolved", label: "Resolved" },
};

const StatusBadge = ({ status }) => {
  const { cls, label } = STATUS_MAP[status] || STATUS_MAP["open"];
  return <span className={`adminDisputes-badge ${cls}`}>{label}</span>;
};

const PriorityTag = ({ priority }) => (
  <span
    className={`adminDisputes-priority adminDisputes-priority--${priority}`}
  >
    {priority}
  </span>
);

/* ══════════════════════════════════════════════
   CHAT PANEL  (inside modal)
══════════════════════════════════════════════ */
const ChatPanel = ({ item, baseUrl }) => {
  const mockMsgs = MOCK_MESSAGES[item.id] || [];
  const [messages, setMessages] = useState(mockMsgs);
  const [apiMessages, setApiMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const displayMessages = apiMessages.length > 0 ? apiMessages : messages;

  /* Scroll to bottom on new message */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayMessages]);

  /* Fetch chat messages for this dispute/ticket */
  const fetchMessages = () => {
    const formData = new FormData();
    formData.append("dispute_id", item.id);

    axios
      .post(`${baseUrl}/admin/disputes/getMessages`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("fetchMessages:", res.data);
        if (res.data.success) setApiMessages(res.data.messages || []);
      })
      .catch((err) => console.log("fetchMessages error:", err.message));
  };

  /* Send a message */
  const sendMessage = () => {
    if (!text.trim()) return;
    setSending(true);

    const formData = new FormData();
    formData.append("dispute_id", item.id);
    formData.append("message", text.trim());
    formData.append("sender", "admin");

    axios
      .post(`${baseUrl}/admin/disputes/sendMessage`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        console.log("sendMessage:", res.data);
        if (res.data.success) {
          setText("");
          fetchMessages();
        }
      })
      .catch((err) => {
        console.log("sendMessage error:", err.message);
        /* Fallback: add locally */
        const newMsg = {
          id: Date.now(),
          sender: "admin",
          name: "Admin",
          text: text.trim(),
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, newMsg]);
        setText("");
      })
      .finally(() => setSending(false));
  };

  /* Fetch on mount */
  useEffect(() => {
    fetchMessages();
  }, [item.id]);

  /* Send on Enter (not Shift+Enter) */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="adminDisputes-chat">
      {/* Messages */}
      <div className="adminDisputes-chat__messages">
        {displayMessages.length === 0 ? (
          <div className="adminDisputes-chat__empty">
            <MessageSquare size={32} />
            <p>No messages yet. Start the conversation.</p>
          </div>
        ) : (
          displayMessages.map((msg) => {
            const isAdmin = msg.sender === "admin";
            return (
              <div
                key={msg.id}
                className={`adminDisputes-chat__bubble-wrap ${isAdmin ? "admin" : "user"}`}
              >
                {/* Avatar */}
                <div
                  className={`adminDisputes-chat__avatar ${isAdmin ? "admin" : "user"}`}
                >
                  {isAdmin ? <ShieldCheck size={14} /> : <User size={14} />}
                </div>
                <div className="adminDisputes-chat__bubble-col">
                  <span className="adminDisputes-chat__name">
                    {msg.name || msg.sender}
                  </span>
                  <div
                    className={`adminDisputes-chat__bubble ${isAdmin ? "admin" : "user"}`}
                  >
                    {msg.text || msg.message}
                  </div>
                  <span className="adminDisputes-chat__time">
                    {msg.time || msg.created_at}
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="adminDisputes-chat__input-row">
        <input
          className="adminDisputes-chat__input"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className="adminDisputes-chat__send-btn"
          onClick={sendMessage}
          disabled={sending || !text.trim()}
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   DETAIL MODAL  (Detail + Chat tabs)
══════════════════════════════════════════════ */
const DetailModal = ({
  item,
  isTicket,
  onClose,
  onResolve,
  loading,
  baseUrl,
}) => {
  const [activeTab, setActiveTab] = useState("detail");
  const [priority, setPriority] = useState(item.priority);

  return (
    <div className="adminDisputes-modal-overlay" onClick={onClose}>
      <div
        className="adminDisputes-modal adminDisputes-modal--with-chat"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Modal Header ── */}
        <div className="adminDisputes-modal-header">
          <h4>
            {isTicket ? "Ticket" : "Dispute"} #{item.id}
          </h4>
          <button className="adminDisputes-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="adminDisputes-modal-tabs">
          <button
            className={`adminDisputes-modal-tab ${activeTab === "detail" ? "active" : ""}`}
            onClick={() => setActiveTab("detail")}
          >
            Details
          </button>
          <button
            className={`adminDisputes-modal-tab ${activeTab === "chat" ? "active" : ""}`}
            onClick={() => setActiveTab("chat")}
          >
            <MessageSquare size={13} /> Chat
          </button>
        </div>

        {/* ── Detail Tab ── */}
        {activeTab === "detail" && (
          <>
            <div className="adminDisputes-modal-grid">
              <div className="adminDisputes-modal-field">
                <label>{isTicket ? "Submitted by" : "Customer"}</label>
                <strong>{item.customer}</strong>
              </div>
              {!isTicket && (
                <div className="adminDisputes-modal-field">
                  <label>Against</label>
                  <strong>{item.against}</strong>
                </div>
              )}
              <div className="adminDisputes-modal-field">
                <label>Date</label>
                <strong>{item.date || item.created_at}</strong>
              </div>
              <div className="adminDisputes-modal-field">
                <label>Status</label>
                <StatusBadge status={item.status} />
              </div>
              <div className="adminDisputes-modal-field full">
                <label>Issue</label>
                <strong>{item.title}</strong>
              </div>
              <div className="adminDisputes-modal-field full">
                <label>Description</label>
                <p className="adminDisputes-modal-desc">{item.description}</p>
              </div>
            </div>

            <label className="adminDisputes-modal-label">Set Priority</label>
            <select
              className="adminDisputes-priority-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            {item.status !== "resolved" && (
              <button
                className="adminDisputes-resolve-btn"
                disabled={loading}
                onClick={() => onResolve(item.id, priority)}
              >
                {loading ? "Resolving..." : "Mark as Resolved"}
              </button>
            )}
          </>
        )}

        {/* ── Chat Tab ── */}
        {activeTab === "chat" && <ChatPanel item={item} baseUrl={baseUrl} />}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const AdminDisputesContent = () => {
  const { baseUrl } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState("disputes");
  const [disputes, setDisputes] = useState(MOCK_DISPUTES);
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [apiDisputes, setApiDisputes] = useState([]);
  const [apiTickets, setApiTickets] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [dispute, setDispute] = useState([]);
  const [closedDisputes, setClosedDispute] = useState();
  const [openDispute, setOpenDispute] = useState();
  const [reviewDispute, setReviewDispute] = useState();

  const displayDisputes = apiDisputes.length > 0 ? apiDisputes : disputes;
  const displayTickets = apiTickets.length > 0 ? apiTickets : tickets;
  const currentList =
    activeTab === "disputes" ? displayDisputes : displayTickets;
  const isTicket = activeTab === "tickets";

  /* Stats */
  const all = [...displayDisputes, ...displayTickets];
  const openDisputes = displayDisputes.filter(
    (d) => d.status === "open",
  ).length;
  const openTickets = displayTickets.filter((t) => t.status === "open").length;
  const inReview = all.filter((i) => i.status === "in-review").length;
  const resolved = all.filter((i) => i.status === "resolved").length;

  /* ════════════════════════════════════════════
     API FUNCTIONS
  ════════════════════════════════════════════ */
  const fetchDisputes = () => {
    axios
      .get(`${baseUrl}/admin/disputes/getAll`)
      .then((res) => {
        if (res.data.success) setApiDisputes(res.data.disputes || []);
      })
      .catch((err) => console.log("fetchDisputes error:", err.message));
  };

  const fetchTickets = () => {
    axios
      .get(`${baseUrl}/admin/tickets/getAll`)
      .then((res) => {
        if (res.data.success) setApiTickets(res.data.tickets || []);
      })
      .catch((err) => console.log("fetchTickets error:", err.message));
  };

  const resolveItem = (id, priority) => {
    setActionLoading(true);
    const endpoint = isTicket
      ? `${baseUrl}/admin/tickets/resolve`
      : `${baseUrl}/admin/disputes/resolve`;

    const formData = new FormData();
    formData.append("id", id);
    formData.append("priority", priority);

    axios
      .post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        if (res.data.success) {
          isTicket ? fetchTickets() : fetchDisputes();
          setSelectedItem(null);
        }
      })
      .catch((err) => {
        console.log("resolveItem error:", err.message);
        if (isTicket) {
          setTickets((prev) =>
            prev.map((t) =>
              t.id === id ? { ...t, status: "resolved", priority } : t,
            ),
          );
        } else {
          setDisputes((prev) =>
            prev.map((d) =>
              d.id === id ? { ...d, status: "resolved", priority } : d,
            ),
          );
        }
        setSelectedItem(null);
      })
      .finally(() => setActionLoading(false));
  };

  // useEffect(() => {
  //   fetchDisputes();
  //   fetchTickets();
  // }, []);

  const viewAllDisputes = () => {
    console.log("view disputes");

    // const formData = new FormData();
    // formData.append("mwcity_id", cityId.id);
    axios
      .get(`${baseUrl}/admin/disputes/viewAllDisputes`, {
        header: {
          "Content-type": "multipart-formdata",
        },
      })
      .then((response) => {
        console.log(response.data);
        if (response.data != null) {
          setDispute(response.data.disputes);
          setClosedDispute(response.data.closed);
          setOpenDispute(response.data.Open);
          setReviewDispute(response.data.review);
        }
        // if(response.data.success == true){
        // if (response.data.areas != null) {
        //   console.log("all areas", response.data.disputes);
        // }

        // }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    // fetchDisputes();
    // fetchTickets();
    viewAllDisputes();
  }, []);

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div className="adminDisputes-page">
      {/* Header */}
      <div className="adminDisputes-header">
        <div>
          <h2>Disputes</h2>
          <p>Manage disputes</p>
        </div>
      </div>

      {/* Stats */}
      <div className="adminDisputes-stats-row">
        {[
          {
            Icon: AlertTriangle,
            color: "red",
            label: "Open Disputes",
            value: openDisputes,
          },
          // { Icon: MessageSquare, color: "blue",   label: "Open Tickets",  value: openTickets  },
          { Icon: Clock, color: "yellow", label: "In Review", value: inReview },
          {
            Icon: CheckCircle,
            color: "green",
            label: "Resolved",
            value: resolved,
          },
        ].map(({ Icon, color, label, value }) => (
          <div className="adminDisputes-stat-card" key={label}>
            <div className={`adminDisputes-stat-icon ${color}`}>
              <Icon size={20} />
            </div>
            <div className="adminDisputes-stat-info">
              {/* <span>{label === "Resolved" ? label  : closedDisputes}</span> */}
              <span>{label}</span>
              <h4>{value}</h4>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="adminDisputes-tabs">
        <button
          className={`adminDisputes-tab-btn ${activeTab === "disputes" ? "active" : ""}`}
          onClick={() => setActiveTab("disputes")}
        >
          Disputes
        </button>
        {/* <button
          className={`adminDisputes-tab-btn ${activeTab === "tickets" ? "active" : ""}`}
          onClick={() => setActiveTab("tickets")}
        >
          Support Tickets
        </button> */}
      </div>

      {/* List */}
      <div className="adminDisputes-list">
        {currentList.length === 0 ? (
          <div className="adminDisputes-empty">
            No {isTicket ? "tickets" : "disputes"} found.
          </div>
        ) : (
          // currentList.map((item) => (
          dispute.map((item) => (
            <div className="adminDisputes-item" key={item.id}>
              <div className="adminDisputes-item-left">
                <div className="adminDisputes-item-meta">
                  <span className="adminDisputes-id">#{item.id}</span>
                  <StatusBadge status={item.status}/>
                  {/* <PriorityTag priority={item.priority} /> */}
                </div>
                {/* <div className="d-flex justify-content-center align-item-center"> */}
                <div className="d-flex justify-content-between">
                  <div className="adminDisputes-item-title">{item.title}</div>
                  <div className="d-flex justify-center align-center">
                    <p>Raised By : </p>
                    <div className="adminDisputes-item-title">
                      {item.raisedby}
                    </div>
                  </div>
                  <div className="d-flex justify-center align-center">
                    <p>Raised to : </p>
                    <div className="adminDisputes-item-title">
                      {item.raisedto}
                    </div>
                  </div>
                  <div className="adminDisputes-item-sub">
                    {isTicket
                      ? `${item.mwcustomer_id} · ${item.date || item.created_at}`
                      : `${item.mwcustomer_id} vs ${item.mwdistributor_id} · ${item.date || item.created_at}`}
                  </div>
                </div>
              </div>
              <button
                className="adminDisputes-view-btn"
                // onClick={() => setSelectedItem(item)}
                onChange={viewAllDisputes}
              >
                View
              </button>
            </div>
          ))
        )}
      </div>

      {/* Detail + Chat Modal */}
      {selectedItem && (
        <DetailModal
          item={selectedItem}
          isTicket={isTicket}
          onClose={() => setSelectedItem(null)}
          onResolve={resolveItem}
          loading={actionLoading}
          baseUrl={baseUrl}
        />
      )}
    </div>
  );
};

const AdminDisputes = () => (
  <AdminMasterLayout>
    <AdminDisputesContent />
  </AdminMasterLayout>
);

export default AdminDisputes;
